// src/components/script-analysis/IPEvaluationResult.jsx
import React from "react";

const ScoreBar = ({ score, theme }) => {
  const pct = Math.max(0, Math.min(100, ((score - 6) / 4) * 100));
  const color = score >= 8.5 ? "var(--color-success)" : score >= 8.0 ? theme.colors.accentPrimary : score >= 7.5 ? "var(--color-warning)" : "var(--color-error)";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--color-bg-tertiary)" }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold w-8 text-right" style={{ color }}>{score}</span>
    </div>
  );
};

const ScoreLabel = ({ score }) => {
  if (score >= 8.5) return <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(61,158,90,0.15)", color: "var(--color-success)" }}>优秀</span>;
  if (score >= 8.0) return <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(201,106,40,0.15)", color: "var(--color-accent-primary)" }}>有潜力</span>;
  if (score >= 7.5) return <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(212,167,68,0.15)", color: "var(--color-warning)" }}>一般</span>;
  return <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: "rgba(217,58,58,0.15)", color: "var(--color-error)" }}>较差</span>;
};

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="text-sm font-bold mb-3 pb-2 border-b" style={{ color: "var(--color-text-primary)", borderColor: "var(--color-border-primary)" }}>{title}</h3>
    {children}
  </div>
);

const DimensionRow = ({ label, item, theme }) => (
  <div className="mb-3">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium" style={{ color: "var(--color-text-secondary)" }}>{label}</span>
      <ScoreLabel score={item?.score || 0} />
    </div>
    <ScoreBar score={item?.score || 0} theme={theme} />
    <p className="text-2xs mt-1 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{item?.analysis}</p>
  </div>
);

const IPEvaluationResult = ({ data, theme }) => {
  if (!data) return null;

  const overall = data.overall || {};

  return (
    <div className="space-y-4">
      {/* Overall Score Card */}
      <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>总体评价</span>
          <div className="flex items-center gap-2">
            <ScoreLabel score={overall.score || 0} />
            <span className="text-2xl font-black" style={{ color: theme.colors.accentPrimary }}>{overall.score || "-"}</span>
          </div>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{overall.analysis}</p>
        {overall.recommendation && (
          <div className="mt-3 px-3 py-2 rounded-lg text-xs font-medium" style={{ backgroundColor: theme.colors.accentLight, color: theme.colors.accentPrimary }}>
            建议：{overall.recommendation}
          </div>
        )}
      </div>

      {/* Basic Info */}
      {data.basicInfo && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <Section title="基本信息">
            {data.basicInfo.synopsis && <p className="text-xs mb-2 leading-relaxed" style={{ color: "var(--color-text-secondary)" }}><strong style={{ color: "var(--color-text-primary)" }}>故事梗概：</strong>{data.basicInfo.synopsis}</p>}
            {data.basicInfo.theme && <p className="text-xs mb-2 leading-relaxed" style={{ color: "var(--color-text-secondary)" }}><strong style={{ color: "var(--color-text-primary)" }}>故事主题：</strong>{data.basicInfo.theme}</p>}
            {data.basicInfo.characters && <p className="text-xs mb-2 leading-relaxed" style={{ color: "var(--color-text-secondary)" }}><strong style={{ color: "var(--color-text-primary)" }}>主要人物：</strong>{data.basicInfo.characters}</p>}
          </Section>
        </div>
      )}

      {/* Market Potential */}
      {data.marketPotential && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <Section title="市场潜力">
            <DimensionRow label="受众适合度" item={data.marketPotential.audienceFit} theme={theme} />
            <DimensionRow label="讨论热度" item={data.marketPotential.discussionHeat} theme={theme} />
            <DimensionRow label="稀缺性" item={data.marketPotential.scarcity} theme={theme} />
            <DimensionRow label="市场前景" item={data.marketPotential.marketProspect} theme={theme} />
          </Section>
        </div>
      )}

      {/* Innovation */}
      {data.innovation && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <Section title="创新属性">
            <DimensionRow label="核心选点" item={data.innovation.coreSelection} theme={theme} />
            <DimensionRow label="故事概念" item={data.innovation.storyConcept} theme={theme} />
            <DimensionRow label="故事设计" item={data.innovation.storyDesign} theme={theme} />
          </Section>
        </div>
      )}

      {/* Content Highlights */}
      {data.contentHighlights && (
        <div className="rounded-xl p-5 border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
          <Section title="内容亮点">
            <DimensionRow label="主题立意" item={data.contentHighlights.theme} theme={theme} />
            <DimensionRow label="故事情境" item={data.contentHighlights.storySituation} theme={theme} />
            <DimensionRow label="人物设定" item={data.contentHighlights.characterDesign} theme={theme} />
            <DimensionRow label="人物关系" item={data.contentHighlights.relationships} theme={theme} />
            <DimensionRow label="情节桥段" item={data.contentHighlights.plotSegments} theme={theme} />
          </Section>
        </div>
      )}
    </div>
  );
};

export default IPEvaluationResult;
